import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  createOrganization,
  updateOrganization,
} from "../../redux/features/organizations/organizationSlice";

import "./OrganizationForm.css";

const initialState = {
  name: "",
  code: "",
  description: "",
  is_active: true,
};

export default function OrganizationForm({
  mode = "create",
  initialData = null,
  onSuccess,
  onCancel,
}) {
  const dispatch = useDispatch();

  const { isCreating, isUpdating, error } = useSelector(
    (state) => state.organizations,
  );

  const [formData, setFormData] = useState(initialState);

  const [validationErrors, setValidationErrors] = useState({});

  const isEditMode = mode === "edit";

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        name: initialData.name || "",
        code: initialData.code || "",
        description: initialData.description || "",
        is_active: initialData.is_active !== false,
      });

      return;
    }

    setFormData(initialState);
  }, [isEditMode, initialData]);

  /**
   * =====================================================
   * CHANGE
   * =====================================================
   */

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /**
   * =====================================================
   * VALIDATE
   * =====================================================
   */

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Naam is verplicht.";
    }

    if (!formData.code.trim()) {
      errors.code = "Code is verplicht.";
    }

    if (formData.code.trim().length < 2) {
      errors.code = "Code is te kort.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /**
   * =====================================================
   * PAYLOAD
   * =====================================================
   */

  const buildPayload = () => ({
    name: formData.name.trim(),

    code: formData.code.trim(),

    description: formData.description.trim(),

    is_active: !!formData.is_active,
  });

  /**
   * =====================================================
   * SUBMIT
   * =====================================================
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payload = buildPayload();

      if (isEditMode) {
        await dispatch(
          updateOrganization({
            id: initialData._id,
            data: payload,
          }),
        ).unwrap();
      } else {
        await dispatch(createOrganization(payload)).unwrap();
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form className="organization-form" onSubmit={handleSubmit}>
      {/* ERROR */}
      {error && <div className="organization-form__error">{error}</div>}

      {/* GRID */}
      <div className="organization-form__grid">
        {/* NAME */}
        <div className="organization-form__group">
          <label>
            Naam <span>*</span>
          </label>

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="TOPunt Gent"
            autoComplete="off"
          />

          {validationErrors.name && <p>{validationErrors.name}</p>}
        </div>

        {/* CODE */}
        <div className="organization-form__group">
          <label>
            Code <span>*</span>
          </label>

          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="topunt-gent"
            autoComplete="off"
          />

          {validationErrors.code && <p>{validationErrors.code}</p>}
        </div>

        {/* DESCRIPTION */}
        <div className="organization-form__group organization-form__group--full">
          <label>Beschrijving</label>

          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Beschrijving van de organisatie..."
            rows={5}
          />
        </div>
      </div>

      {/* ACTIVE */}
      <label className="organization-form__checkbox">
        <input
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <span>Organisatie actief</span>
      </label>

      {/* ACTIONS */}
      <div className="organization-form__actions">
        <button
          type="button"
          className="organization-form__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuleren
        </button>

        <button
          type="submit"
          className="organization-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Opslaan..."
            : isEditMode
              ? "Wijzigingen opslaan"
              : "Organisatie aanmaken"}
        </button>
      </div>
    </form>
  );
}
